/**
 * Reusable structured "Event" block editor.
 * Each event has a specific date, time, full address, image, recurrence.
 */
import { Calendar, Trash2, Plus } from "lucide-react";
import FileUpload from "@/components/churchbuilder/FileUpload";

const inp = "w-full bg-black border border-white/15 focus:border-[#D4AF37] outline-none px-3 py-2.5 rounded text-sm text-white";

const newEvent = () => ({
  _id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).slice(2),
  title: "",
  date: "",
  start_time: "18:00",
  end_time: "20:00",
  recurrence: "none", // none, weekly, monthly, custom
  recurrence_note: "",
  venue_name: "",
  address_line1: "",
  address_line2: "",
  city: "",
  postcode: "",
  country: "United Kingdom",
  maps_url: "",
  description: "",
  image_url: "",
  published: true,
});

export default function EventsBlockEditor({ churchId, items = [], onChange }) {
  const update = (i, patch) => {
    const arr = [...items];
    arr[i] = { ...arr[i], ...patch };
    onChange(arr);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, newEvent()]);

  return (
    <div data-testid="block-events" className="luxury-card rounded-xl p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-serif-display text-xl flex items-center gap-2"><Calendar size={16} className="text-[#D4AF37]" /> Events</div>
          <div className="text-xs text-neutral-500 mt-1">One-off, weekly, monthly or custom — with date, time, address and image.</div>
        </div>
        <button data-testid="block-ev-add" onClick={add} className="btn-ghost-gold text-[10px] tracking-widest uppercase px-3 py-1.5 rounded inline-flex items-center gap-1"><Plus size={11} /> Add Event</button>
      </div>

      <div className="mt-4 space-y-4">
        {items.map((row, i) => (
          <div key={row._id || `ev-${i}`} data-testid={`block-ev-row-${i}`} className="border border-white/5 rounded-lg p-4 bg-black/30 space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Event title"><input data-testid={`ev-${i}-title`} value={row.title || ""} onChange={(e) => update(i, { title: e.target.value })} className={inp} placeholder="Christmas Carol Service" /></Field>
              <Field label="Date"><input data-testid={`ev-${i}-date`} type="date" value={row.date || ""} onChange={(e) => update(i, { date: e.target.value })} className={inp} /></Field>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Start time"><input data-testid={`ev-${i}-start`} type="time" value={row.start_time || ""} onChange={(e) => update(i, { start_time: e.target.value })} className={inp} /></Field>
              <Field label="End time"><input data-testid={`ev-${i}-end`} type="time" value={row.end_time || ""} onChange={(e) => update(i, { end_time: e.target.value })} className={inp} /></Field>
              <Field label="Recurrence">
                <select data-testid={`ev-${i}-recurrence`} value={row.recurrence || "none"} onChange={(e) => update(i, { recurrence: e.target.value })} className={inp}>
                  <option value="none">One-off</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="custom">Custom</option>
                </select>
              </Field>
            </div>
            {row.recurrence === "custom" && (
              <Field label="Custom recurrence note (e.g. every 2 weeks, last Friday of month)">
                <input data-testid={`ev-${i}-custom`} value={row.recurrence_note || ""} onChange={(e) => update(i, { recurrence_note: e.target.value })} className={inp} />
              </Field>
            )}

            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Venue name"><input value={row.venue_name || ""} onChange={(e) => update(i, { venue_name: e.target.value })} className={inp} placeholder="Church Auditorium" /></Field>
              <Field label="Address line 1"><input value={row.address_line1 || ""} onChange={(e) => update(i, { address_line1: e.target.value })} className={inp} /></Field>
              <Field label="Address line 2"><input value={row.address_line2 || ""} onChange={(e) => update(i, { address_line2: e.target.value })} className={inp} /></Field>
              <Field label="City"><input value={row.city || ""} onChange={(e) => update(i, { city: e.target.value })} className={inp} /></Field>
              <Field label="Postcode"><input value={row.postcode || ""} onChange={(e) => update(i, { postcode: e.target.value })} className={inp} /></Field>
              <Field label="Country"><input value={row.country || ""} onChange={(e) => update(i, { country: e.target.value })} className={inp} /></Field>
            </div>
            <Field label="Google Maps link"><input value={row.maps_url || ""} onChange={(e) => update(i, { maps_url: e.target.value })} className={inp} placeholder="https://maps.google.com/..." /></Field>
            <Field label="Description"><textarea rows={3} value={row.description || ""} onChange={(e) => update(i, { description: e.target.value })} className={inp} /></Field>
            <FileUpload churchId={churchId} value={row.image_url} onChange={(url) => update(i, { image_url: url })} label="Event image" testid={`ev-${i}-image`} />

            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 text-xs text-neutral-300">
                <input type="checkbox" checked={!!row.published} onChange={(e) => update(i, { published: e.target.checked })} /> Published
              </label>
              <button data-testid={`ev-${i}-remove`} onClick={() => remove(i)} className="text-red-400 text-xs tracking-widest uppercase inline-flex items-center gap-1"><Trash2 size={11} /> Remove</button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-xs text-neutral-500">No events yet — click Add Event.</div>}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] tracking-widest uppercase text-neutral-400">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
